import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useProvider } from "./useProvider";

interface OpportunityClaim {
  id: string;
  opportunity_id: string;
  status: string;
  reason_code: string;
  reason_text: string | null;
  operator_note: string | null;
  created_at: string;
  decided_at: string | null;
}

interface Opportunity {
  id: string;
  opportunity_number: string;
  request_id: string;
  provider_id: string;
  match_score: number;
  match_reasons: string[];
  score_breakdown: Record<string, any> | null;
  price: number;
  currency: string;
  status: "new" | "in_review" | "completed";
  question_used: boolean;
  question_text: string | null;
  question_answer: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  expires_at: string | null;
  outcome: "won" | "lost" | "no_fit" | null;
  outcome_reason: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  request?: {
    id: string;
    request_number: string;
    category: string;
    urgency: string;
    context: string | null;
    decision_authority: string;
    location: string | null;
    remote_allowed: boolean;
    budget_range: string | null;
    requester_company: string | null;
    requester_email?: string;
    contact_phone?: string | null;
    compliance_relevant?: boolean;
    onsite_required?: boolean;
  };
  question?: {
    id: string;
    question_text: string;
    status: string;
    answer_text: string | null;
  } | null;
  contact_reveal?: {
    contact_snapshot: Record<string, any>;
    revealed_at: string;
  } | null;
  claim?: OpportunityClaim | null;
}

export const useOpportunities = () => {
  const { profile } = useProvider();
  const queryClient = useQueryClient();

  const { data: opportunities = [], isLoading } = useQuery({
    queryKey: ["opportunities", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      const { data, error } = await supabase
        .from("opportunities")
        .select(`
          *,
          request:requests(
            id,
            request_number,
            category,
            urgency,
            context,
            decision_authority,
            location,
            remote_allowed,
            budget_range,
            requester_company,
            requester_email,
            contact_phone,
            compliance_relevant,
            onsite_required,
            contact_data
          )
        `)
        .eq("provider_id", profile.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch questions, contact reveals, and claims separately
      const oppIds = (data || []).map(o => o.id);
      
      const [questionsResult, revealsResult, claimsResult] = await Promise.all([
        oppIds.length > 0
          ? supabase.from("opportunity_questions").select("*").in("opportunity_id", oppIds)
          : Promise.resolve({ data: [] }),
        oppIds.length > 0
          ? supabase.from("contact_reveals").select("*").eq("revealed_to_provider_user_id", profile.user_id)
          : Promise.resolve({ data: [] }),
        oppIds.length > 0
          ? supabase.from("opportunity_claims").select("id, opportunity_id, status, reason_code, reason_text, operator_note, created_at, decided_at").in("opportunity_id", oppIds)
          : Promise.resolve({ data: [] }),
      ]);

      const questions = (questionsResult as any).data || [];
      const reveals = (revealsResult as any).data || [];
      const claims = (claimsResult as any).data || [];

      return (data || []).map(opp => ({
        ...opp,
        match_reasons: Array.isArray(opp.match_reasons) ? opp.match_reasons : [],
        score_breakdown: opp.score_breakdown || null,
        question: questions.find((q: any) => q.opportunity_id === opp.id) || null,
        contact_reveal: reveals.find((r: any) => r.opportunity_id === opp.id) || null,
        claim: claims.find((c: any) => c.opportunity_id === opp.id) || null,
        // Backwards compat: map question fields from separate table
        question_used: !!questions.find((q: any) => q.opportunity_id === opp.id),
        question_text: questions.find((q: any) => q.opportunity_id === opp.id)?.question_text || opp.question_text,
        question_answer: questions.find((q: any) => q.opportunity_id === opp.id)?.answer_text || opp.question_answer,
      })) as Opportunity[];
    },
    enabled: !!profile?.id,
  });

  // Realtime: refetch when opportunity_claims change for this provider
  useEffect(() => {
    if (!profile?.id) return;
    const channel = supabase
      .channel("claims-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "opportunity_claims",
          filter: `provider_id=eq.${profile.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["opportunities"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, queryClient]);

  const acceptOpportunity = useMutation({
    mutationFn: async (opportunityId: string) => {
      const { data, error } = await supabase.functions.invoke("accept-opportunity", {
        body: { opportunity_id: opportunityId, action: "accept" },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      queryClient.invalidateQueries({ queryKey: ["provider-profile"] });
    },
  });

  const declineOpportunity = useMutation({
    mutationFn: async (opportunityId: string) => {
      const { data, error } = await supabase.functions.invoke("accept-opportunity", {
        body: { opportunity_id: opportunityId, action: "decline" },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });

  const askQuestion = useMutation({
    mutationFn: async ({ opportunityId, question }: { opportunityId: string; question: string }) => {
      // Insert into separate opportunity_questions table (UNIQUE constraint enforces 1/1)
      const { error } = await supabase
        .from("opportunity_questions")
        .insert({
          opportunity_id: opportunityId,
          provider_id: profile!.id,
          question_text: question,
        });

      if (error) {
        if (error.code === "23505") {
          throw new Error("Rückfrage bereits genutzt (1/1)");
        }
        throw error;
      }

      // Also update legacy field for backwards compat
      await supabase
        .from("opportunities")
        .update({ question_used: true, question_text: question })
        .eq("id", opportunityId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });

  const completeOpportunity = useMutation({
    mutationFn: async ({
      opportunityId,
      outcome,
      reason,
    }: {
      opportunityId: string;
      outcome: "won" | "lost" | "no_fit";
      reason?: string;
    }) => {
      const { error } = await supabase
        .from("opportunities")
        .update({
          status: "completed",
          outcome,
          outcome_reason: reason,
          completed_at: new Date().toISOString(),
        })
        .eq("id", opportunityId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });

  const submitClaim = useMutation({
    mutationFn: async ({ opportunityId, reasonCode, reasonText }: { opportunityId: string; reasonCode: string; reasonText?: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/provider-api/opportunities/${opportunityId}/claim`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ reason_code: reasonCode, reason_text: reasonText }),
        }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error(err.error || resp.statusText);
      }
      return resp.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });

  const newOpportunities = opportunities.filter(o => o.status === "new" && !o.declined_at);
  const claimedOpportunities = opportunities.filter(o => o.claim && !["APPROVED", "approved", "REJECTED", "rejected"].includes(o.claim.status));
  const inReviewOpportunities = opportunities.filter(o => o.status === "in_review" && !claimedOpportunities.some(c => c.id === o.id));
  const completedOpportunities = opportunities.filter(o => o.status === "completed");
  const declinedOpportunities = opportunities.filter(o => !!o.declined_at);

  return {
    opportunities,
    newOpportunities,
    inReviewOpportunities,
    claimedOpportunities,
    completedOpportunities,
    declinedOpportunities,
    isLoading,
    acceptOpportunity,
    declineOpportunity,
    askQuestion,
    completeOpportunity,
    submitClaim,
  };
};
